import React, { useState } from "react";
import { prepFqReportStyles } from "../styles/prepFqReportStyles";
import { useTranslation } from "next-i18next";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import CardHeader from "@mui/material/CardHeader";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowRightIcon from "../icons/ArrowRightIcon";
import StartPrioritiesModal from "./StartPrioritiesModal";
import { useAuth } from "../../../firebase/useAuth";
import useUser from "../../../firebase/useUser";
import { useLearningModules } from "../fetchers/learning_modules/useLearningModules";
import useUserLearningModules from "../fetchers/useUserLearningModules";
import Image from "next/image";
import clsx from "clsx";
import usePrepFqProfile from "../fetchers/usePrepFqProfile";
import Link from "next/link";
import { GUIDES_ENHANCE, ENHANCE_PRIORITIES } from "../../../constants/routes";

interface Props {
  setTopicModalOpen(boolean): any;
  setSelectedModuleId(string): any;
}

const PrioritiesBanner: React.FC<Props> = ({
  setTopicModalOpen,
  setSelectedModuleId,
}) => {
  const { t } = useTranslation(["prepFq"]);
  const classes = prepFqReportStyles();
  const [open, setOpen] = useState(false);
  const { learningModules, allQuestions } = useLearningModules();
  const authUser = useAuth();
  const { user } = useUser(authUser?.user?.uid);
  const { userModules } = useUserLearningModules(
    user?.user_id,
    user?.learning_module_priorities?.map((priority) => priority.module_id)
  );
  const { profile } = usePrepFqProfile(user?.user_id);

  return (
    <div className={classes.root}>
      {user?.has_learning_module_priorities && (
        <div className={classes.prioritiesTitleLarge}>
          <Button
            href={GUIDES_ENHANCE}
            variant="contained"
            color="secondary"
            className={clsx(classes.enhanceBackButton)}
          >
            <ArrowBackIcon />
            &thinsp; Back
          </Button>
          My Priorities
          <Button
            href={ENHANCE_PRIORITIES}
            variant="contained"
            color="secondary"
            className={clsx(
              classes.settingsButton,
              "eighteenth-step-pick-priorities"
            )}
          >
            <ArrowBackIcon />
            &thinsp; Back
          </Button>
          {user?.has_learning_module_priorities && (
            <Button
              href={ENHANCE_PRIORITIES}
              variant="contained"
              color="secondary"
              className={clsx(
                classes.settingsButton,
                "eighteenth-step-pick-priorities"
              )}
            >
              <SettingsIcon />
              &thinsp;{t("prepFq:report.priorities.manage")}
            </Button>
          )}
        </div>
      )}
      {!user?.has_learning_module_priorities && (
        <div className={classes.promptContainer}>
          <h2 className={classes.prioritiesTitle}>
            {t("prepFq:report.priorities.prompt")}
          </h2>
          <div>
            <Link passHref href={ENHANCE_PRIORITIES}>
              <Button
                color="secondary"
                variant="contained"
                size="large"
                className={clsx(
                  "eighteenth-step-pick-priorities",
                  classes.pickPrioritiesButton
                )}
                style={{ textTransform: "capitalize" }}
              >
                {t("prepFq:report.priorities.button")}
              </Button>
            </Link>
          </div>
        </div>
      )}
      <StartPrioritiesModal open={open} setOpen={setOpen} />
      {user?.has_learning_module_priorities && (
        <div className={classes.prioritiesContainer}>
          {userModules?.map((userModule) => {
            const learningModule = learningModules.find((learning_module) =>
              userModule.module_id.includes(learning_module.module_id)
            );
            const question = allQuestions?.find((question) =>
              question.module_id.includes(learningModule?.module_id)
            );
            const prepFqResult = profile?.scored_results?.find(
              (result) => result.module_id === userModule.module_id
            );
            return (
              <Card
                key={learningModule?.module_id}
                className={classes.priorityCard}
                elevation={5}
              >
                <CardHeader
                  classes={{
                    root: classes.priorityCardHeader,
                    title: classes.priorityCardTitle,
                  }}
                  title={learningModule?.module_name.toUpperCase()}
                />
                <CardContent className={classes.priorityCardContent}>
                  <div className={classes.priorityProgressContainer}>
                    <div className={classes.priorityProgress}>
                      <p className={classes.priorityProgressText}>
                        {Math.round(prepFqResult?.scaled_score)}
                        <span className={classes.percentage}>%</span>
                      </p>
                    </div>
                  </div>
                  <div className={classes.priorityIconContainer}>
                    <Image
                      alt={`${learningModule.module_name} Icon`}
                      src={learningModule?.icon_url}
                      layout={"fixed"}
                      width={72}
                      height={72}
                    />
                  </div>
                </CardContent>
                <CardActions className={classes.priorityActions}>
                  <Button
                    onClick={() => {
                      setSelectedModuleId(question?.module_id);
                      setTopicModalOpen(true);
                    }}
                    className={classes.priorityActionButton}
                  >
                    <ArrowRightIcon />
                    &thinsp;{t("prepFq:report.priorities.go")}
                  </Button>
                </CardActions>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PrioritiesBanner;
